import {
  ConnectionRequestIdPublic as ConnectionRequestIdPublicBrand,
  EventIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { ConnectionRequestPartyView } from "@shared/schemas/connection-request-views.schemas";
import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import type { RespondToRequestActionType } from "@frontend/shared/data/repositories/directory/directory-repository-options.types";
import { useGatedAction } from "../gated-action";
import { gateEnabled } from "../gate-enabled";
import type { EventView } from "./use-event-actions";

type DirectoryHooks = FrontendDAL["directory"];

export type ConnectionRequestIdPublic = Parameters<
  DirectoryHooks["useGetContactInfo"]
>[0]["requestIdPublic"];

export interface ConnectionRequestActionsContext {
  readonly event: EventView | undefined;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Accept/decline this request — the event and request ids are prefilled from the
 * node's scope, so `.mutate`/`.mutateAsync` take only the `action`.
 */
export interface RespondToRequestMutation {
  readonly mutate: (input: {
    readonly action: RespondToRequestActionType;
  }) => void;
  readonly mutateAsync: (input: {
    readonly action: RespondToRequestActionType;
  }) => Promise<ConnectionRequestPartyView>;
  readonly isPending: boolean;
}

/** Mark this request seen — ids prefilled, so `.mutate`/`.mutateAsync` take nothing. */
export interface MarkIntroSeenMutation {
  readonly mutate: () => void;
  readonly mutateAsync: () => Promise<ConnectionRequestPartyView>;
  readonly isPending: boolean;
}

export interface ConnectionRequestQueries {
  readonly useGetContactInfo: (options?: {
    readonly enabled?: boolean;
  }) => ReturnType<DirectoryHooks["useGetContactInfo"]>;
}

interface ConnectionRequestExtra extends ConnectionRequestQueries {
  readonly respond: RespondToRequestMutation;
  readonly markSeen: MarkIntroSeenMutation;
}

export interface ConnectionRequestActions extends ConnectionRequestExtra {
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Connection-request actions — mirrors the backend `ConnectionRequestActions`
 * reached via `EventActions.forConnectionRequest(requestId)`. Scoped to a single
 * `requestIdPublic`, it bundles all three per-request ops the backend keeps on that
 * actor: the `useGetContactInfo` read plus the `updateStatus` mutations `respond`
 * (accept/decline) and `markSeen`. All are gated by participant + resolved event
 * (finer per-request authorization is the backend's 403, surfaced by the DAL). The
 * mutations prefill the event + request ids from the node's scope; list callers that
 * fire across many requests instantiate one node per row.
 */
export function useConnectionRequestActions(
  context: ConnectionRequestActionsContext,
  requestIdPublic: ConnectionRequestIdPublic,
): ConnectionRequestActions {
  const eventIdPublic =
    context.event?.idPublic ?? EventIdPublic.unsafeCreate("");
  // The DAL's updateStatus variables type `requestIdPublic` as a raw string
  // (unlike the opaque-branded `getContactInfo` param), so unwrap once here.
  const requestId = ConnectionRequestIdPublicBrand.unwrap(requestIdPublic);

  const gated = useGatedAction({
    useDeps: () => ({
      respondMutation: dal().directory.useRespondToRequest(),
      markSeenMutation: dal().directory.useMarkIntroSeen(),
    }),
    isLoading: () => context.isLoading,
    error: () => context.error,
    authorize: () => {
      const event = context.event;
      if (event === undefined) return null;
      return event.$roles.includes("event:participant") ? event : null;
    },
    extra: (d, { isAuthorized }): ConnectionRequestExtra => ({
      useGetContactInfo: (options) =>
        dal().directory.useGetContactInfo({
          eventIdPublic,
          requestIdPublic,
          enabled: gateEnabled(isAuthorized, options?.enabled),
        }),
      respond: {
        isPending: d.respondMutation.isPending,
        mutate: ({ action }): void => {
          d.respondMutation.mutate({
            eventIdPublic,
            requestIdPublic: requestId,
            action,
          });
        },
        mutateAsync: ({ action }) =>
          d.respondMutation.mutateAsync({
            eventIdPublic,
            requestIdPublic: requestId,
            action,
          }),
      },
      markSeen: {
        isPending: d.markSeenMutation.isPending,
        mutate: (): void => {
          d.markSeenMutation.mutate({
            eventIdPublic,
            requestIdPublic: requestId,
          });
        },
        mutateAsync: () =>
          d.markSeenMutation.mutateAsync({
            eventIdPublic,
            requestIdPublic: requestId,
          }),
      },
    }),
  });

  return {
    isLoading: gated.isLoading,
    error: gated.error,
    useGetContactInfo: gated.useGetContactInfo,
    respond: gated.respond,
    markSeen: gated.markSeen,
  };
}
